import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, CheckCircle, TrendingUp, Shield, Users, Target, BarChart3, Compass, Clock } from 'lucide-react';

export const Home = () => {
  const { t } = useTranslation();

  const stats = [
    { title: t('stats.brokerage.title'), subtitle: t('stats.brokerage.subtitle') },
    { title: t('stats.energy.title'), subtitle: t('stats.energy.subtitle') },
    { title: t('stats.analysis.title'), subtitle: t('stats.analysis.subtitle') },
  ];

  const services = [
    {
      icon: BarChart3,
      title: t('services.financial.title'),
      text: t('services.financial.text'),
      span: 'lg:col-span-2',
    },
    {
      icon: TrendingUp,
      title: t('services.project.title'),
      text: t('services.project.text'),
      span: '',
    },
    {
      icon: Compass,
      title: t('services.sector.title'),
      text: t('services.sector.text'),
      span: '',
    },
  ];

  const steps = [
    { icon: Target, title: t('approach.step1.title'), text: t('approach.step1.text'), num: '01' },
    { icon: BarChart3, title: t('approach.step2.title'), text: t('approach.step2.text'), num: '02' },
    { icon: Shield, title: t('approach.step3.title'), text: t('approach.step3.text'), num: '03' },
    { icon: Clock, title: t('approach.step4.title'), text: t('approach.step4.text'), num: '04' },
  ];

  const reasons = [
    t('whyUs.reason1'),
    t('whyUs.reason2'),
    t('whyUs.reason3'),
    t('whyUs.reason4'),
    t('whyUs.reason5'),
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center"
      >
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1770473859769-d5a601e54a63?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwbmlnaHR8ZW58MHx8fHwxNzcwNjcxNTcyfDA&ixlib=rb-4.1.0&q=85"
            alt="Corporate skyline"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1A2E]/60 via-[#0F1A2E]/80 to-[#0F1A2E]" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none mb-8">
              {t('hero.title')}
            </h1>
            <p className="text-white/70 text-lg md:text-xl max-w-2xl mb-12 leading-relaxed">
              {t('hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="hero-cta"
                className="group inline-flex items-center justify-center gap-3 bg-[#C9A84C] text-[#0F1A2E] px-8 py-4 font-semibold tracking-wide hover:bg-[#E5D49B] transition-all duration-300"
              >
                {t('hero.cta')}
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="hero-learn-more"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-white px-8 py-4 font-semibold tracking-wide hover:bg-white/5 hover:border-[#C9A84C]/50 transition-all duration-300"
              >
                {t('hero.learnMore')}
              </Link>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-20 grid grid-cols-3 gap-8 max-w-2xl"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-left border-l-2 border-[#C9A84C]/30 pl-4">
                <p className="font-heading text-lg md:text-xl font-semibold text-[#C9A84C]">
                  {stat.title}
                </p>
                <p className="text-white/60 text-sm mt-1">{stat.subtitle}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
          >
            <div className="w-1.5 h-3 bg-[#C9A84C] rounded-full mt-2" />
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section data-testid="about-preview-section" className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
                {t('about.title')}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1B2B4B] mb-6">
                {t('about.subtitle')}
              </h2>
              <p className="text-[#1B2B4B]/70 text-lg leading-relaxed mb-8">
                {t('about.description')}
              </p>
              <Link
                to="/about"
                data-testid="about-link"
                className="group inline-flex items-center gap-3 text-[#C9A84C] font-semibold hover:gap-5 transition-all duration-300"
              >
                {t('hero.learnMore')}
                <ArrowUpRight className="group-hover:rotate-45 transition-transform" />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-lg shadow-xl">
                <img
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                  alt="Business meeting"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 max-w-xs shadow-lg border border-[#C9A84C]/20">
                <Users className="text-[#C9A84C] mb-3" size={28} />
                <p className="text-[#1B2B4B] font-semibold">{t('values.trust.title')}</p>
                <p className="text-[#1B2B4B]/60 text-sm mt-2">{t('values.trust.text')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-testid="services-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              {t('services.title')}
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1B2B4B]">
              {t('services.subtitle')}
            </h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className={`group relative overflow-hidden bg-[#F8F6F3] border border-[#1B2B4B]/10 p-8 hover:border-[#C9A84C] hover:shadow-lg transition-all duration-500 ${service.span}`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#C9A84C]/5 rounded-full blur-3xl group-hover:bg-[#C9A84C]/10 transition-all duration-500" />
                <service.icon className="text-[#C9A84C] mb-6" size={40} />
                <h3 className="font-heading text-2xl font-semibold text-[#1B2B4B] mb-4">
                  {service.title}
                </h3>
                <p className="text-[#1B2B4B]/60 leading-relaxed">
                  {service.text}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              data-testid="services-link"
              className="group inline-flex items-center gap-3 border-2 border-[#C9A84C] text-[#C9A84C] px-8 py-4 font-semibold tracking-wide hover:bg-[#C9A84C] hover:text-white transition-all duration-300"
            >
              {t('hero.learnMore')}
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section data-testid="approach-section" className="py-24 md:py-32 bg-[#1B2B4B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
              {t('approach.title')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`approach-step-${index}`}
                className="relative group"
              >
                <div className="text-[#C9A84C]/20 font-heading text-7xl font-bold absolute -top-4 -left-2 group-hover:text-[#C9A84C]/30 transition-colors">
                  {step.num}
                </div>
                <div className="relative pt-12">
                  <step.icon className="text-[#C9A84C] mb-4" size={28} />
                  <h3 className="font-heading text-xl font-semibold text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-4">
                {t('whyUs.title')}
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[#1B2B4B] mb-8">
                {t('whyUs.title')}
              </h2>
              <div className="space-y-6">
                {reasons.map((reason, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <CheckCircle className="text-[#C9A84C] flex-shrink-0 mt-1" size={22} />
                    <p className="text-[#1B2B4B]/70">{reason}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=600&q=80"
                  alt="Energy sector"
                  className="w-full aspect-square object-cover rounded-lg shadow-lg"
                />
                <img
                  src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?w=600&q=80"
                  alt="Maritime sector"
                  className="w-full aspect-square object-cover rounded-lg shadow-lg mt-8"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white p-6 shadow-lg border border-[#C9A84C]/20">
                <p className="font-heading text-xl font-semibold text-[#C9A84C]">{t('stats.analysis.title')}</p>
                <p className="text-[#1B2B4B]/60 text-sm">{t('stats.analysis.subtitle')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-[#1B2B4B] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGdvbGQlMjBnZW9tZXRyaWMlMjBzaGFwZXMlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzA2NzE1Nzd8MA&ixlib=rb-4.1.0&q=85"
            alt="Abstract background"
            className="w-full h-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A2E] via-transparent to-[#0F1A2E]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('footer.letsTalk')}
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto mb-10">
              {t('contact.description')}
            </p>
            <Link
              to="/contact"
              data-testid="final-cta"
              className="group inline-flex items-center gap-4 bg-[#C9A84C] text-[#0F1A2E] px-10 py-5 font-semibold text-lg tracking-wide hover:bg-[#E5D49B] transition-all duration-300"
            >
              {t('hero.cta')}
              <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
